import { applyLabelKeywordEdit, type LabelKeywordEdit } from './messagePatches';
import type {
  ConversationSortOrder,
  ConversationSummary,
  ConversationSyncResult,
  ConversationSyncRow,
  FlagUpdate,
  MessageFlag,
  Paginated,
} from '@/api/types';

/**
 * Pure patches over the conversation lists (ADR 0077, plan/16 §8).
 *
 * The same rules as `messagePatches.ts`, whose sibling this is: no React, no
 * TanStack Query, and **the identical reference back when nothing changed** —
 * the list is virtualized, and a fresh array on a no-op re-renders the window.
 *
 * A conversation list is keyed by conversation, but every verb and every live
 * event names MESSAGES. So each transform here takes message ids and works out
 * which rows they touch: a row whose members are all removed leaves; a row
 * that loses some keeps its place with fewer members; a flag change on some
 * members is folded into the row's counts as far as the row can know.
 */

/** One page of a conversation list, as the cache holds it. */
export interface ConversationListPage extends Paginated<ConversationSummary> {
  /** A folder's list: the `state` token its rows were last reconciled to (§5). */
  state?: string;
  /** The merged list: one token per lane (account), for the same purpose. */
  states?: Readonly<Record<string, string>>;
}

/** The shape `useInfiniteQuery` stores for a conversation list. */
export interface ConversationPageCache {
  pages: ConversationListPage[];
  pageParams: unknown[];
}

/**
 * Whether a cached value is a conversation list rather than a message list.
 * Both are `{ pages: [{ items }] }`; only a conversation carries `messageIds`.
 */
export function isConversationPageCache(value: unknown): value is ConversationPageCache {
  if (typeof value !== 'object' || value === null) return false;
  const pages = (value as { pages?: unknown }).pages;
  if (!Array.isArray(pages)) return false;
  return pages.some(
    (page: { items?: unknown }) =>
      Array.isArray(page?.items) &&
      page.items.some(
        (item: unknown) =>
          typeof item === 'object' && item !== null && Array.isArray((item as ConversationSummary).messageIds),
      ),
  );
}

function ownedBy(row: ConversationSummary, accountId: string | undefined): boolean {
  return accountId === undefined || row.accountId === accountId;
}

function mapRows(
  cache: ConversationPageCache,
  fn: (row: ConversationSummary) => ConversationSummary | undefined,
): ConversationPageCache {
  let changed = false;
  const pages = cache.pages.map((page) => {
    let pageChanged = false;
    let dropped = 0;
    const items: ConversationSummary[] = [];
    for (const row of page.items) {
      const next = fn(row);
      if (next !== row) pageChanged = true;
      if (next === undefined) dropped += 1;
      else items.push(next);
    }
    if (!pageChanged) return page;
    changed = true;
    return {
      ...page,
      items,
      ...(page.total !== undefined && dropped > 0 ? { total: Math.max(0, page.total - dropped) } : {}),
    };
  });
  return changed ? { ...cache, pages } : cache;
}

/* ---------------------------------------------------------------- removal */

/**
 * Messages left the folder (archive, delete, move, spam, expunge).
 *
 * A row whose every member left goes; a row that lost some keeps its place
 * with the rest — its head falls back to the newest remaining member, so
 * opening it never names a message that has gone. The next `sync` puts the
 * server's own figures back.
 */
export function removeMessagesFromConversations(
  cache: ConversationPageCache | undefined,
  messageIds: readonly string[],
  accountId?: string,
): ConversationPageCache | undefined {
  if (!cache || messageIds.length === 0) return cache;
  const ids = new Set(messageIds);
  return mapRows(cache, (row) => {
    if (!ownedBy(row, accountId)) return row;
    const remaining = row.messageIds.filter((id) => !ids.has(id));
    if (remaining.length === row.messageIds.length) return row;
    if (remaining.length === 0) return undefined;
    const gone = row.messageIds.length - remaining.length;
    const unreadCount = Math.min(row.unreadCount, remaining.length);
    return {
      ...row,
      messageIds: remaining,
      messageCount: Math.max(remaining.length, row.messageCount - gone),
      unreadCount,
      newestMessageId: remaining.includes(row.newestMessageId)
        ? row.newestMessageId
        : remaining[remaining.length - 1]!,
      flags: withSeen(row.flags, unreadCount === 0),
    };
  });
}

/* ------------------------------------------------------------------- flags */

function withSeen(flags: readonly MessageFlag[], seen: boolean): MessageFlag[] {
  const has = flags.includes('seen');
  if (has === seen) return flags as MessageFlag[];
  return seen ? [...flags, 'seen'] : flags.filter((flag) => flag !== 'seen');
}

/**
 * The flag half of a conversation's summary, after `update` reached `covered`
 * of its members.
 *
 * `seen` is counted: the row knows how many members are unread, not which, so
 * marking some read takes that many off (never below zero) and marking every
 * member read is exact. Any other flag is "any member has it": adding it to
 * one member adds it to the row, and removing it clears the row only when
 * every member was covered — another member may still carry it.
 */
export function patchConversationSummaryFlags(
  row: ConversationSummary,
  covered: number,
  update: FlagUpdate,
): ConversationSummary {
  if (covered === 0) return row;
  const all = covered >= row.messageIds.length;
  let unreadCount = row.unreadCount;
  const add = update.add ?? [];
  const remove = update.remove ?? [];
  if (add.includes('seen')) unreadCount = all ? 0 : Math.max(0, unreadCount - covered);
  if (remove.includes('seen')) {
    unreadCount = all ? row.messageIds.length : Math.min(row.messageIds.length, unreadCount + covered);
  }
  let flags: MessageFlag[] = row.flags.filter(
    (flag) => flag === 'seen' || !remove.includes(flag) || !all,
  );
  for (const flag of add) if (flag !== 'seen' && !flags.includes(flag)) flags.push(flag);
  flags = withSeen(flags, unreadCount === 0);

  const participants =
    unreadCount === 0 && row.participants.some((p) => p.unread)
      ? row.participants.map((p) => (p.unread ? { ...p, unread: false } : p))
      : row.participants;

  const sameFlags =
    flags.length === row.flags.length && flags.every((flag, index) => flag === row.flags[index]);
  if (sameFlags && unreadCount === row.unreadCount && participants === row.participants) return row;
  return { ...row, flags, unreadCount, participants };
}

/** A flag change on messages, applied to every conversation row holding them. */
export function patchConversationFlags(
  cache: ConversationPageCache | undefined,
  messageIds: readonly string[],
  update: FlagUpdate,
  accountId?: string,
): ConversationPageCache | undefined {
  if (!cache || messageIds.length === 0) return cache;
  const ids = new Set(messageIds);
  return mapRows(cache, (row) => {
    if (!ownedBy(row, accountId)) return row;
    let covered = 0;
    for (const id of row.messageIds) if (ids.has(id)) covered += 1;
    return patchConversationSummaryFlags(row, covered, update);
  });
}

/* ------------------------------------------------------------------- labels */

/**
 * A label change on messages, folded into the conversation rows holding them
 * (plan/17 §5). A row's `labelKeywords` is the union over ALL its messages —
 * in-folder members and merged Sent — so:
 *
 * - an `add` reaching any of them adds to the union;
 * - a `remove` clears the union only when it reached every one of them
 *   (another message may still carry the label; the next `sync` says);
 * - a `rename` is exact either way;
 * - an absolute `set` is one message's whole set, which says nothing about the
 *   union: the stream's frames are left to the list's `sync`, as flags are.
 */
export function patchConversationLabels(
  cache: ConversationPageCache | undefined,
  messageIds: readonly string[] | undefined,
  edit: LabelKeywordEdit,
  accountId?: string,
): ConversationPageCache | undefined {
  if (!cache || edit.set !== undefined) return cache;
  const ids = messageIds ? new Set(messageIds) : undefined;
  return mapRows(cache, (row) => {
    if (!ownedBy(row, accountId)) return row;
    const everyone = [...row.messageIds, ...(row.sentMessageIds ?? [])];
    const covered = ids === undefined ? everyone.length : everyone.filter((id) => ids.has(id)).length;
    if (covered === 0) return row;
    const all = covered >= everyone.length;
    const labelKeywords = applyLabelKeywordEdit(row.labelKeywords, {
      ...(edit.add ? { add: edit.add } : {}),
      ...(edit.remove && all ? { remove: edit.remove } : {}),
      ...(edit.rename ? { rename: edit.rename } : {}),
    });
    return labelKeywords === row.labelKeywords ? row : { ...row, labelKeywords };
  });
}

/* --------------------------------------------------------------------- sync */

function dateOf(row: ConversationSummary): number {
  const value = Date.parse(row.date);
  return Number.isNaN(value) ? 0 : value;
}

/**
 * The list's order: by the head's date, newest first unless asked otherwise.
 * Ties break on account then id, so a merged list is a total order and equal
 * rows never swap places between two syncs (the lane merge's own rule).
 */
export function compareConversations(
  sort: ConversationSortOrder,
): (a: ConversationSummary, b: ConversationSummary) => number {
  const sign = sort === 'date-asc' ? 1 : -1;
  return (a, b) => {
    const byDate = sign * (dateOf(a) - dateOf(b));
    if (byDate !== 0) return byDate;
    if (a.accountId !== b.accountId) return a.accountId < b.accountId ? -1 : 1;
    if (a.id !== b.id) return a.id < b.id ? -1 : 1;
    return 0;
  };
}

/**
 * Whether two summaries of one conversation say the same thing. A sync
 * re-reports a row whenever anything about it moved on the server — the echo
 * of this tab's own mark-read, say, which the optimistic patch already
 * painted. Keeping the held object then keeps the row (and the list) from
 * re-rendering for nothing. Summaries are small and come from one encoder,
 * so their JSON is a faithful comparison.
 */
export function sameConversation(a: ConversationSummary, b: ConversationSummary): boolean {
  return a === b || JSON.stringify(a) === JSON.stringify(b);
}

/** The rows `sync` is told about: what the client holds, first ones first, ≤ `max`. */
export function conversationSyncRows(
  cache: ConversationPageCache | undefined,
  accountId?: string,
  max = 500,
): ConversationSyncRow[] {
  const rows: ConversationSyncRow[] = [];
  const seen = new Set<string>();
  for (const page of cache?.pages ?? []) {
    for (const row of page.items) {
      if (rows.length >= max) return rows;
      if (!ownedBy(row, accountId) || seen.has(row.id)) continue;
      seen.add(row.id);
      rows.push({ id: row.id, newestMessageId: row.newestMessageId, messageCount: row.messageCount });
    }
  }
  return rows;
}

/**
 * The token to hand `sync`: the one the held rows were last reconciled to.
 *
 * The FIRST page's, never a later page's. A page fetched while scrolling is
 * built from a newer folder state, but the rows above it were not reconciled
 * to that state — syncing from it would skip whatever changed up there in
 * between (a new conversation at the top, say). Starting from the older token
 * only re-reports rows the later page already has, which is harmless.
 */
export function conversationSyncState(
  cache: ConversationPageCache | undefined,
  accountId?: string,
): string | undefined {
  const first = cache?.pages[0];
  if (!first) return undefined;
  return accountId === undefined ? first.state : first.states?.[accountId];
}

export interface ApplySyncOptions {
  sort: ConversationSortOrder;
  /**
   * More pages exist below the loaded ones. A row whose (new) date sorts
   * below the last loaded row is then left out of the loaded pages: it
   * belongs to a page not yet fetched, and will arrive with it (§2). That
   * holds for a row the list already had, too — in `date-asc` a row that
   * gains mail moves DOWN, past what is loaded, not to the end of it.
   */
  hasMore: boolean;
  /**
   * The lane this answer is for, in the merged list. Only its rows are
   * compared against `removed`, and the new token is filed under it.
   */
  laneAccountId?: string;
}

/**
 * One `sync` answer, applied in place (plan/16 §5, §8).
 *
 * - `removed` rows leave.
 * - An upsert the list holds is replaced and moved to its date position —
 *   new mail joining a conversation takes it to the top (or, oldest first,
 *   toward the bottom).
 * - An upsert the list does not hold is inserted at its date position, when
 *   that position is within what is loaded.
 * - The new token is stored on the first page, for the next sync.
 *
 * No row is re-created that did not change, and the list is never emptied
 * and re-filled: this is what "nothing blanks" means for the live path. An
 * answer that changes nothing hands back the same cache; one that only moves
 * the token replaces the first page's object and nothing under it.
 */
export function applyConversationSync(
  cache: ConversationPageCache | undefined,
  result: Pick<ConversationSyncResult, 'state' | 'upserts' | 'removed'>,
  options: ApplySyncOptions,
): ConversationPageCache | undefined {
  if (!cache || cache.pages.length === 0) return cache;
  const lane = options.laneAccountId;
  const compare = compareConversations(options.sort);

  const heldRows = new Map<string, ConversationSummary>();
  for (const page of cache.pages) {
    for (const row of page.items) if (ownedBy(row, lane)) heldRows.set(row.id, row);
  }
  const removed = new Set(result.removed.filter((id) => heldRows.has(id)));
  const upserts = new Map<string, ConversationSummary>();
  for (const row of result.upserts) {
    if (lane !== undefined && row.accountId !== lane) continue;
    const held = heldRows.get(row.id);
    // An echo of what the list already shows is not a change.
    if (held !== undefined && sameConversation(held, row)) continue;
    upserts.set(row.id, row);
  }

  if (removed.size === 0 && upserts.size === 0) {
    const first = cache.pages[0]!;
    const current = lane === undefined ? first.state : first.states?.[lane];
    if (current === result.state) return cache;
    const pages = [...cache.pages];
    pages[0] = withToken(first, lane, result.state);
    return { ...cache, pages };
  }

  // The rows, flattened with the page each one lives on.
  const entries: { row: ConversationSummary; page: number }[] = [];
  let dropped = 0;
  cache.pages.forEach((page, pageIndex) => {
    for (const row of page.items) {
      const mine = ownedBy(row, lane);
      if (mine && (removed.has(row.id) || upserts.has(row.id))) {
        if (!upserts.has(row.id)) dropped += 1;
        continue;
      }
      entries.push({ row, page: pageIndex });
    }
  });
  const lastLoaded = entries[entries.length - 1]?.row;
  const lastPage = cache.pages.length - 1;

  let added = 0;
  for (const row of upserts.values()) {
    const isHeld = heldRows.has(row.id);
    // Past the loaded rows while more pages exist: the page that holds it
    // will bring it (a held row leaves its old place and is not counted out
    // of the total — it is still in the folder).
    if (options.hasMore && lastLoaded !== undefined && compare(row, lastLoaded) > 0) continue;
    let at = entries.findIndex((entry) => compare(row, entry.row) < 0);
    if (at === -1) at = entries.length;
    const page = at < entries.length ? entries[at]!.page : lastPage;
    entries.splice(at, 0, { row, page });
    if (!isHeld) added += 1;
  }

  const pages = cache.pages.map((page, pageIndex) => {
    const items = entries.filter((entry) => entry.page === pageIndex).map((entry) => entry.row);
    const same =
      items.length === page.items.length && items.every((row, index) => row === page.items[index]);
    let next: ConversationListPage = same ? page : { ...page, items };
    if (pageIndex === 0) {
      const total =
        page.total !== undefined ? Math.max(0, page.total + added - dropped) : undefined;
      next = withToken(total !== undefined ? { ...next, total } : next, lane, result.state);
    }
    return next;
  });
  return { ...cache, pages };
}

function withToken(
  page: ConversationListPage,
  lane: string | undefined,
  state: string,
): ConversationListPage {
  return lane === undefined
    ? { ...page, state }
    : { ...page, states: { ...(page.states ?? {}), [lane]: state } };
}

/* ---------------------------------------------------------------------- undo */

/**
 * Undo, for a conversation list: the rows the patch took away (or thinned)
 * come back as they were, at the page and index they had, in the list's
 * CURRENT state — everything else that changed meanwhile is kept. A list
 * dropped from the cache in the meantime stays dropped.
 */
export function reinsertConversations(
  now: ConversationPageCache | undefined,
  before: ConversationPageCache,
  messageIds: ReadonlySet<string>,
  accountId?: string,
): ConversationPageCache | undefined {
  if (!now || now.pages.length === 0) return now;
  const key = (row: ConversationSummary) => `${row.accountId}\u0000${row.id}`;
  const present = new Map<string, ConversationSummary>();
  for (const page of now.pages) for (const row of page.items) present.set(key(row), row);
  let pages = now.pages;
  const copy = () => {
    if (pages === now.pages) pages = [...now.pages];
  };

  before.pages.forEach((page, pageIndex) => {
    page.items.forEach((row, itemIndex) => {
      if (!ownedBy(row, accountId) || !row.messageIds.some((id) => messageIds.has(id))) return;
      const current = present.get(key(row));
      if (current === row) return;
      if (current !== undefined) {
        // Thinned rather than removed: put the members back.
        if (row.messageIds.every((id) => current.messageIds.includes(id))) return;
        copy();
        pages = pages.map((candidate) =>
          candidate.items.includes(current)
            ? { ...candidate, items: candidate.items.map((item) => (item === current ? row : item)) }
            : candidate,
        );
        present.set(key(row), row);
        return;
      }
      copy();
      const target = Math.min(pageIndex, pages.length - 1);
      const into = pages[target]!;
      const items = [...into.items];
      items.splice(Math.min(itemIndex, items.length), 0, row);
      pages[target] = {
        ...into,
        items,
        ...(into.total !== undefined ? { total: into.total + 1 } : {}),
      };
      present.set(key(row), row);
    });
  });

  return pages === now.pages ? now : { ...now, pages };
}
